/**
 * Chat Page
 *
 * Interactive chat interface with NIΛS and DΛST widget sidebars
 * Supports variable search and dynamic widget placement
 */

import React, { useState, useRef, useEffect } from 'react'
import { MessageSquare, Send, Hash, Sparkles, Settings, ChevronLeft, ChevronRight } from 'lucide-react'
import { NIASWidget } from '../components/widgets/NIASWidget'
import { DASTWidget } from '../components/widgets/DASTWidget'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  variables?: string[]
}

type WidgetPosition = 'left' | 'right' | 'none'

interface ActiveWidget {
  type: 'nias' | 'dast'
  position: WidgetPosition
}

export const ChatPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'system',
      content: 'Welcome to LUKHAS Chat. I can help you with development tasks, code analysis, and more. Type @search followed by a variable name to activate DΛST tracking.',
      timestamp: new Date(),
    },
  ])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [activeWidgets, setActiveWidgets] = useState<ActiveWidget[]>([])
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true)
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Mock user ID (in production, get from auth)
  const userId = 'demo-user'

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Parse message for commands
  const parseMessage = (text: string): { content: string; commands: string[] } => {
    const commands: string[] = []
    let content = text

    // Extract @search commands
    const searchMatches = text.match(/@search\s+(\w+)/g)
    if (searchMatches) {
      searchMatches.forEach((match) => {
        const variable = match.replace('@search', '').trim()
        commands.push(`search:${variable}`)
      })
    }

    // Extract @nias command
    if (text.includes('@nias')) {
      commands.push('nias')
      content = content.replace('@nias', '').trim()
    }

    // Extract @dast command
    if (text.includes('@dast')) {
      commands.push('dast')
      content = content.replace('@dast', '').trim()
    }

    return { content, commands }
  }

  // Handle variable search from DAST widget
  const handleVariableSearch = (variable: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: `Search for variable: ${variable}`,
      timestamp: new Date(),
      variables: [variable],
    }
    setMessages((prev) => [...prev, newMessage])

    // Simulate AI response
    setTimeout(() => {
      const response: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `I found ${Math.floor(Math.random() * 10) + 1} occurrences of "${variable}" in the codebase. The variable is primarily used in the following contexts:\n\n1. Symbol tracking in DΛST core\n2. Context snapshot generation\n3. Lambda fingerprint calculation\n\nWould you like me to show specific code examples?`,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, response])
    }, 1500)
  }

  // Toggle widget
  const toggleWidget = (type: 'nias' | 'dast', position: WidgetPosition) => {
    setActiveWidgets((prev) => {
      const existing = prev.find((w) => w.type === type)

      if (existing) {
        // Remove widget
        return prev.filter((w) => w.type !== type)
      } else {
        // Add widget
        return [...prev, { type, position }]
      }
    })
  }

  // Handle send message
  const handleSendMessage = async () => {
    if (!inputValue.trim()) return

    const { content, commands } = parseMessage(inputValue)

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: content || inputValue,
      timestamp: new Date(),
    }
    setMessages((prev) => [...prev, userMessage])
    setInputValue('')
    setIsTyping(true)

    // Process commands
    commands.forEach((command) => {
      if (command.startsWith('search:')) {
        const variable = command.replace('search:', '')
        handleVariableSearch(variable)

        // Auto-open DAST widget if not already open
        const dastWidget = activeWidgets.find((w) => w.type === 'dast')
        if (!dastWidget) {
          toggleWidget('dast', 'right')
        }
      } else if (command === 'nias') {
        // Toggle NIAS widget
        toggleWidget('nias', 'left')
      } else if (command === 'dast') {
        // Toggle DAST widget
        toggleWidget('dast', 'right')
      }
    })

    // Simulate AI response
    setTimeout(() => {
      const response: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: commands.length > 0
          ? 'I\'ve processed your commands. The widgets have been updated accordingly.'
          : `I understand you said: "${content}". How can I help you with that?`,
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, response])
      setIsTyping(false)
    }, 1500)
  }

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Get active widgets for each position
  const leftWidget = activeWidgets.find((w) => w.position === 'left')
  const rightWidget = activeWidgets.find((w) => w.position === 'right')

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      {/* Left Sidebar */}
      {leftWidget && leftSidebarOpen && (
        <div className="w-80 flex-shrink-0 relative">
          <button
            onClick={() => setLeftSidebarOpen(false)}
            className="absolute -right-3 top-4 z-10 p-1 bg-gray-800 border border-gray-700 rounded-full hover:bg-gray-700 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-gray-400" />
          </button>

          {leftWidget.type === 'nias' && (
            <NIASWidget
              userId={userId}
              position="left"
              onClose={() => toggleWidget('nias', 'left')}
            />
          )}
          {leftWidget.type === 'dast' && (
            <DASTWidget
              userId={userId}
              position="left"
              onClose={() => toggleWidget('dast', 'left')}
              onVariableSearch={handleVariableSearch}
            />
          )}
        </div>
      )}

      {/* Left Sidebar Toggle (when closed) */}
      {leftWidget && !leftSidebarOpen && (
        <button
          onClick={() => setLeftSidebarOpen(true)}
          className="fixed left-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-gray-800 border-r border-gray-700 rounded-r-lg hover:bg-gray-700 transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">LUKHAS Chat</h1>
                  <p className="text-xs text-gray-400">Λ Consciousness-Aware Assistant</p>
                </div>
              </div>

              {/* Widget Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => toggleWidget('nias', leftWidget ? 'left' : 'left')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all ${
                    activeWidgets.find((w) => w.type === 'nias')
                      ? 'bg-purple-500/20 border-purple-500/30 text-purple-400'
                      : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:bg-gray-800'
                  }`}
                  title="Toggle NIΛS widget"
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="text-sm">NIΛS</span>
                </button>

                <button
                  onClick={() => toggleWidget('dast', rightWidget ? 'right' : 'right')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all ${
                    activeWidgets.find((w) => w.type === 'dast')
                      ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400'
                      : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:bg-gray-800'
                  }`}
                  title="Toggle DΛST widget"
                >
                  <Hash className="w-4 h-4" />
                  <span className="text-sm">DΛST</span>
                </button>

                <button
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                  title="Chat settings"
                >
                  <Settings className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-2xl ${
                  message.role === 'user'
                    ? 'bg-blue-500/20 border-blue-500/30'
                    : message.role === 'system'
                    ? 'bg-gray-800/50 border-gray-700'
                    : 'bg-gray-800/50 border-gray-700'
                } border rounded-lg p-4`}
              >
                {/* Message Header */}
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`text-xs font-medium ${
                      message.role === 'user'
                        ? 'text-blue-400'
                        : message.role === 'system'
                        ? 'text-yellow-400'
                        : 'text-purple-400'
                    }`}
                  >
                    {message.role === 'user' ? 'You' : message.role === 'system' ? 'System' : 'LUKHAS'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {message.timestamp.toLocaleTimeString()}
                  </span>
                </div>

                {/* Message Content */}
                <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
                  {message.content}
                </p>

                {/* Variables */}
                {message.variables && message.variables.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {message.variables.map((variable, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded border border-cyan-500/30"
                      >
                        #{variable}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 max-w-2xl">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                  <span className="text-xs text-gray-400">LUKHAS is thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-800 bg-gray-900/50 backdrop-blur-sm p-4">
          <div className="max-w-4xl mx-auto">
            {/* Command Hints */}
            <div className="mb-3 flex flex-wrap gap-2 text-xs text-gray-400">
              <span className="px-2 py-1 bg-gray-800/50 rounded">
                <code>@search variable</code> - Search variable in DΛST
              </span>
              <span className="px-2 py-1 bg-gray-800/50 rounded">
                <code>@nias</code> - Toggle NIΛS widget
              </span>
              <span className="px-2 py-1 bg-gray-800/50 rounded">
                <code>@dast</code> - Toggle DΛST widget
              </span>
            </div>

            {/* Input Box */}
            <div className="relative">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message... (Shift+Enter for new line)"
                className="w-full px-4 py-3 pr-12 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={3}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim()}
                className="absolute right-2 bottom-2 p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                title="Send message (Enter)"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar */}
      {rightWidget && rightSidebarOpen && (
        <div className="w-80 flex-shrink-0 relative">
          <button
            onClick={() => setRightSidebarOpen(false)}
            className="absolute -left-3 top-4 z-10 p-1 bg-gray-800 border border-gray-700 rounded-full hover:bg-gray-700 transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </button>

          {rightWidget.type === 'nias' && (
            <NIASWidget
              userId={userId}
              position="right"
              onClose={() => toggleWidget('nias', 'right')}
            />
          )}
          {rightWidget.type === 'dast' && (
            <DASTWidget
              userId={userId}
              position="right"
              onClose={() => toggleWidget('dast', 'right')}
              onVariableSearch={handleVariableSearch}
            />
          )}
        </div>
      )}

      {/* Right Sidebar Toggle (when closed) */}
      {rightWidget && !rightSidebarOpen && (
        <button
          onClick={() => setRightSidebarOpen(true)}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-gray-800 border-l border-gray-700 rounded-l-lg hover:bg-gray-700 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-400" />
        </button>
      )}
    </div>
  )
}

export default ChatPage
